import React from "react";
import _ from "lodash";
import { suggestCategory } from "../../../../../data/ShoppingCategories";

/**
 * Custom hook to manage meal plan details and associated data
 *
 * @param {Object} mealPlan - The meal plan object
 * @param {Function} getRecipesFromList - Function to fetch recipes from a list
 * @returns {Object} State and handler functions for meal plan details
 */
const useMealPlanDetails = (mealPlan, getRecipesFromList) => {
  const [recipes, setRecipes] = React.useState([]);
  const [scheduledRecipes, setScheduledRecipes] = React.useState([]);
  const [ingredients, setIngredients] = React.useState([]);
  const [weeklySchedule, setWeeklySchedule] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);

  // Fetch recipes when meal plan changes
  React.useEffect(() => {
    if (!mealPlan || !mealPlan.selectedRecipes || !Array.isArray(mealPlan.selectedRecipes)) {
      setScheduledRecipes([]);
      setRecipes([]);
      return;
    }

    console.log("useMealPlanDetails: Processing meal plan:", mealPlan);
    console.log("useMealPlanDetails: Selected recipes:", mealPlan.selectedRecipes);

    // Set loading state
    setIsLoading(true);

    // Pass selectedRecipes directly since getRecipesFromList expects objects with recipeId property
    getRecipesFromList(mealPlan.selectedRecipes)
      .then((fetchedRecipes) => {
        // Filter out any undefined/null recipes
        const validRecipes = (fetchedRecipes || []).filter(recipe => recipe != null);
        
        setRecipes(validRecipes);

        // Create scheduled recipes with both recipe data and scheduling info
        const scheduledRecipesData = mealPlan.selectedRecipes.map(selectedRecipe => {
          // Match using the recipe's "id" property, not the document ID
          const recipe = validRecipes.find(r => r.id === selectedRecipe.recipeId);
          if (!recipe) {
            console.warn(`Recipe not found for recipeId: ${selectedRecipe.recipeId}`);
          }
          return {
            ...selectedRecipe,
            recipe: recipe || null
          };
        }).filter(scheduledRecipe => scheduledRecipe.recipe !== null);

        setScheduledRecipes(scheduledRecipesData);
      })
      .catch((error) => {
        console.error("Error fetching recipes:", error);
        setRecipes([]);
        setScheduledRecipes([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [mealPlan, getRecipesFromList]);

  // Process ingredients and weekly schedule when scheduled recipes change
  React.useEffect(() => {
    // Skip if no scheduled recipes
    if (!scheduledRecipes || scheduledRecipes.length === 0) {
      setIngredients([]);
      setWeeklySchedule({});
      return;
    }

    // Create weekly schedule
    const schedule = {
      monday: { mealTypes: { breakfast: [], lunch: [], dinner: [], snack: [], other: [] } },
      tuesday: { mealTypes: { breakfast: [], lunch: [], dinner: [], snack: [], other: [] } },
      wednesday: { mealTypes: { breakfast: [], lunch: [], dinner: [], snack: [], other: [] } },
      thursday: { mealTypes: { breakfast: [], lunch: [], dinner: [], snack: [], other: [] } },
      friday: { mealTypes: { breakfast: [], lunch: [], dinner: [], snack: [], other: [] } },
      saturday: { mealTypes: { breakfast: [], lunch: [], dinner: [], snack: [], other: [] } },
      sunday: { mealTypes: { breakfast: [], lunch: [], dinner: [], snack: [], other: [] } }
    };

    // Helper function to map short day names to full day names
    const dayMapping = {
      'mon': 'monday',
      'tue': 'tuesday', 
      'wed': 'wednesday',
      'thu': 'thursday',
      'fri': 'friday',
      'sat': 'saturday',
      'sun': 'sunday',
      'monday': 'monday',
      'tuesday': 'tuesday',
      'wednesday': 'wednesday', 
      'thursday': 'thursday',
      'friday': 'friday',
      'saturday': 'saturday',
      'sunday': 'sunday'
    };

    // Get all ingredients from scheduled recipes
    const allIngredients = [];
    
    scheduledRecipes.forEach((scheduledRecipe) => {
      const { recipe, days, servingsPerDay } = scheduledRecipe;
      
      // Safety check: ensure recipe exists and has ingredients
      if (!recipe || !recipe.ingredients || !Array.isArray(recipe.ingredients)) {
        return;
      }

            // Add recipe to appropriate days in schedule
      if (days && Array.isArray(days)) {
        if (days.includes("all")) {
          // If recipe is available all days, add it to each day of the week
          const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          allDays.forEach(dayKey => {
            const mealType = recipe.mealType || 'other';
            schedule[dayKey].mealTypes[mealType].push({
              ...scheduledRecipe,
              displayName: `${recipe.name} (${servingsPerDay || 1} serving${(servingsPerDay || 1) > 1 ? 's' : ''})`
            });
          });
        } else {
          days.forEach(day => {
            const dayKey = dayMapping[day.toLowerCase()];
            if (dayKey && schedule[dayKey]) {
              const mealType = recipe.mealType || 'other';
              schedule[dayKey].mealTypes[mealType].push({
                ...scheduledRecipe,
                displayName: `${recipe.name} (${servingsPerDay || 1} serving${(servingsPerDay || 1) > 1 ? 's' : ''})`
              });
            }
          });
        }
      }

      // Calculate ingredients with servings multiplier
      const servingMultiplier = servingsPerDay || 1;
      
      recipe.ingredients.forEach((ingredient) => {
        // Safety check: ensure ingredient exists and has required properties
        if (ingredient && ingredient.name && ingredient.unit) {
          const adjustedIngredient = {
            ...ingredient,
            amount: (ingredient.amount || 0) * servingMultiplier,
            sourceRecipe: recipe.name,
            servings: servingMultiplier,
            // Ensure category is set, using auto-suggestion if not provided
            category: ingredient.category || suggestCategory(ingredient.name),
          };
          allIngredients.push(adjustedIngredient);
        }
      });
    });

    // Set weekly schedule
    setWeeklySchedule(schedule);

    // Calculate daily nutrition totals (assuming 1 recipe chosen from each meal type per day)
    const dailyNutritionTotals = {};
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    allDays.forEach(day => {
      const dayData = schedule[day];
      const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
      
      // Calculate nutrition for each meal type (average if multiple options)
      const dayTotals = {
        breakfast: { kCals: 0, proteins: 0, carbs: 0, fats: 0, fiber: 0, hasOptions: false },
        lunch: { kCals: 0, proteins: 0, carbs: 0, fats: 0, fiber: 0, hasOptions: false },
        dinner: { kCals: 0, proteins: 0, carbs: 0, fats: 0, fiber: 0, hasOptions: false },
        snack: { kCals: 0, proteins: 0, carbs: 0, fats: 0, fiber: 0, hasOptions: false },
        total: { kCals: 0, proteins: 0, carbs: 0, fats: 0, fiber: 0 }
      };
      
      mealTypes.forEach(mealType => {
        const mealRecipes = dayData.mealTypes[mealType] || [];
        if (mealRecipes.length > 0) {
          dayTotals[mealType].hasOptions = true;
          
          // Calculate average nutrition for this meal type (since client picks 1)
          let totalKCals = 0, totalProteins = 0, totalCarbs = 0, totalFats = 0, totalFiber = 0;
          
          mealRecipes.forEach(scheduledRecipe => {
            const { recipe, servingsPerDay } = scheduledRecipe;
            const multiplier = servingsPerDay || 1;
            const fiberG =
              parseFloat(recipe.fiber) ||
              parseFloat(recipe.mac?.fi) ||
              parseFloat(recipe.mac?.fiber) ||
              0;
            
            totalKCals += (parseFloat(recipe.kCals) || 0) * multiplier;
            totalProteins += (parseFloat(recipe.proteins) || 0) * multiplier;
            totalCarbs += (parseFloat(recipe.carbs) || 0) * multiplier;
            totalFats += (parseFloat(recipe.fats) || 0) * multiplier;
            totalFiber += fiberG * multiplier;
          });
          
          // Average the values (since client will choose 1 from available options)
          const numOptions = mealRecipes.length;
          dayTotals[mealType].kCals = totalKCals / numOptions;
          dayTotals[mealType].proteins = totalProteins / numOptions;
          dayTotals[mealType].carbs = totalCarbs / numOptions;
          dayTotals[mealType].fats = totalFats / numOptions;
          dayTotals[mealType].fiber = totalFiber / numOptions;
          
          // Add to daily total
          dayTotals.total.kCals += dayTotals[mealType].kCals;
          dayTotals.total.proteins += dayTotals[mealType].proteins;
          dayTotals.total.carbs += dayTotals[mealType].carbs;
          dayTotals.total.fats += dayTotals[mealType].fats;
          dayTotals.total.fiber += dayTotals[mealType].fiber;
        }
      });
      
      dailyNutritionTotals[day] = dayTotals;
    });

    // Calculate overall averages for header display
    const daysWithData = allDays.filter(day => {
      const dayTotal = dailyNutritionTotals[day]?.total;
      return dayTotal && (dayTotal.kCals > 0 || dayTotal.proteins > 0 || dayTotal.carbs > 0 || dayTotal.fats > 0 || dayTotal.fiber > 0);
    });
    
    const overallAverages = { kCals: 0, proteins: 0, carbs: 0, fats: 0, fiber: 0 };
    if (daysWithData.length > 0) {
      daysWithData.forEach(day => {
        const dayTotal = dailyNutritionTotals[day].total;
        overallAverages.kCals += dayTotal.kCals;
        overallAverages.proteins += dayTotal.proteins;
        overallAverages.carbs += dayTotal.carbs;
        overallAverages.fats += dayTotal.fats;
        overallAverages.fiber += dayTotal.fiber;
      });
      
      overallAverages.kCals = overallAverages.kCals / daysWithData.length;
      overallAverages.proteins = overallAverages.proteins / daysWithData.length;
      overallAverages.carbs = overallAverages.carbs / daysWithData.length;
      overallAverages.fats = overallAverages.fats / daysWithData.length;
      overallAverages.fiber = overallAverages.fiber / daysWithData.length;
    }

    // Store both weekly schedule and nutrition data
    setWeeklySchedule({
      ...schedule,
      dailyNutritionTotals,
      overallAverages
    });

    // Get total amount of each ingredient
    const ingredientMap = {};
    allIngredients.forEach((ingredient) => {
      const key = `${ingredient.name}${ingredient.unit}`;
      if (ingredientMap[key]) {
        ingredientMap[key].amount += ingredient.amount;
        // Track which recipes contribute to this ingredient
        if (!ingredientMap[key].sourceRecipes) {
          ingredientMap[key].sourceRecipes = [];
        }
        if (!ingredientMap[key].sourceRecipes.includes(ingredient.sourceRecipe)) {
          ingredientMap[key].sourceRecipes.push(ingredient.sourceRecipe);
        }
      } else {
        ingredientMap[key] = {
          ..._.cloneDeep(ingredient),
          sourceRecipes: [ingredient.sourceRecipe]
        };
      }
    });

    // Convert to array
    const ingredientArray = [];
    Object.keys(ingredientMap).forEach((key) => {
      ingredientArray.push(ingredientMap[key]);
    });

    setIngredients(ingredientArray);
  }, [scheduledRecipes]);

  return {
    recipes,
    scheduledRecipes,
    ingredients,
    weeklySchedule,
    dailyNutritionTotals: weeklySchedule.dailyNutritionTotals,
    overallAverages: weeklySchedule.overallAverages,
    isLoading,
  };
};

export default useMealPlanDetails;
